import { useId, type ReactNode } from 'react';
import { cn } from './cn';

/**
 * A titled group of fields inside a long form.
 *
 * The console's forms are long — a student record has identity, contact, consent
 * and enrolment on one screen — and an undifferentiated stack of forty inputs is
 * how a form gets abandoned. Grouping is not decoration: the heading is what
 * lets someone scan to "consent", and it is a real `<section>` with an
 * `aria-labelledby`, so it is a landmark a screen-reader user can jump between
 * rather than a bold line of text.
 *
 * `hint` is the slot for the thing that is true of the whole group — the
 * retention rule for a consent block, the note that a field is public-facing.
 * Put it here rather than repeating it under every field.
 */

export interface FormSectionProps {
  title: string;
  /** One line under the heading: what this group is for. */
  description?: ReactNode;
  /**
   * Aside content — a retention note, a policy link, a warning that these fields
   * are public. Rendered beside the fields on wide screens, above them below
   * 1024px so it is read before the fields rather than after.
   */
  hint?: ReactNode;
  /** Section-level actions, e.g. "Add another". */
  actions?: ReactNode;
  children: ReactNode;
  className?: string;
}

export function FormSection({
  title,
  description,
  hint,
  actions,
  children,
  className,
}: FormSectionProps) {
  const headingId = useId();
  return (
    <section
      aria-labelledby={headingId}
      className={cn(
        'border-b-[0.5px] border-hairline py-lg first:pt-0 last:border-b-0 last:pb-0',
        className,
      )}
    >
      <div className="flex flex-wrap items-baseline justify-between gap-sm">
        <h2 id={headingId} className="text-component-title text-ink-deep">
          {title}
        </h2>
        {actions}
      </div>
      {description && <p className="mt-2xs max-w-prose text-small text-ink-mid">{description}</p>}

      <div className="mt-md grid gap-lg lg:grid-cols-[minmax(0,1fr)_260px]">
        {hint ? (
          <>
            {/* Source order puts the hint first so it is announced and read
                before the fields; `lg:order-2` moves it to the side visually.
                It is a sidenote on a rule, not a boxed card: a `FormSection`
                normally sits INSIDE a `Card`, and a bordered, rounded panel in
                there is a card inside a card — two competing boundaries for one
                piece of content, which is the read the console's own surfaces
                rule forbids. */}
            <aside className="border-s-2 border-hairline ps-md text-small text-ink-mid lg:order-2">
              {hint}
            </aside>
            <div className="flex min-w-0 flex-col gap-md lg:order-1">{children}</div>
          </>
        ) : (
          <div className="flex min-w-0 flex-col gap-md lg:col-span-2">{children}</div>
        )}
      </div>
    </section>
  );
}

/**
 * Two or three fields that belong on one line (a date range, a city/country
 * pair). Collapses to a stack below 768px, where side-by-side inputs are how a
 * form gets too narrow to read.
 */
export function FormRow({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div className={cn('grid gap-md sm:grid-cols-2', className)}>{children}</div>
  );
}
